import { PageContainer, PageHeader, Skeleton } from '@ccp/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AppErrorState } from '../../app/error-state.js';
import { useQueueBoard } from '../../features/supervision/api.js';
import { QueueBoardTable } from '../../features/supervision/components/queue-board.js';
import { QueueDrilldown } from '../../features/supervision/components/queue-drilldown.js';
import { SuperviseTabs } from '../../features/supervision/components/supervise-tabs.js';
import { record, type Series } from '../../features/supervision/samples.js';
import { useBoardTopics } from '../../features/supervision/use-board-topics.js';

/**
 * What the address bar carries, validated like any other boundary.
 *
 * `queueIds` narrows what is shown and `period` says what the figures are over, and both are in the
 * URL rather than in a store so that a supervisor can send somebody the board they are looking at.
 * Anything that is not a uuid is dropped rather than refused: a mistyped link should show the whole
 * board, not an error page.
 */
const searchSchema = z.object({
  queueIds: z.array(z.uuid()).catch([]).default([]),
  period: z.enum(['hour', 'shift', 'today']).catch('today').default('today'),
});

export const Route = createFileRoute('/supervise/queues')({
  validateSearch: searchSchema,
  component: QueueBoardScreen,
});

/**
 * The queue board.
 *
 * The figures are the live counters — routing's own queue entries and the presence fast path — read
 * at the dashboard staleness budget. `period` is in the URL and does **not** yet change them: the
 * interval statistics a period selects over are reporting's (M8), and the control is here, saying
 * what it will mean, rather than invented client-side out of numbers that have no history.
 */
function QueueBoardScreen() {
  const { t } = useTranslation('supervision');
  const { queueIds } = Route.useSearch();
  const board = useQueueBoard();
  const [openQueueId, setOpenQueueId] = useState<string | undefined>(undefined);

  // This session's own readings of the counters, which is what the sparklines draw until M8's
  // interval projection exists. `dataUpdatedAt` rather than `Date.now()`: one reading per fetch,
  // so a re-render does not become a sample.
  const [series, setSeries] = useState<Series>({});
  const items = board.data?.items;
  const updatedAt = board.dataUpdatedAt;
  useEffect(() => {
    if (items !== undefined && updatedAt > 0) {
      setSeries((current) => record(current, items, updatedAt));
    }
  }, [items, updatedAt]);

  useBoardTopics(
    'queue',
    useMemo(() => (items ?? []).map((row) => row.queueId), [items]),
  );

  const open = items?.find((row) => row.queueId === openQueueId);

  return (
    <PageContainer width="full">
      {/* The area is the heading and its two views are tabs under it. It was the other way round —
          two plain links and then a heading repeating whichever was open — and nothing said
          which of the two links was the current one (M5.5-01). */}
      <PageHeader title={t('title')} />
      <div className="flex flex-col gap-(--space-sm)">
        <SuperviseTabs />
        <p className="text-(length:--font-size-body) text-(--color-text-secondary)">
          {t('queues.description')}
        </p>
      </div>

      {board.isError ? (
        <AppErrorState error={board.error} resetErrorBoundary={() => void board.refetch()} />
      ) : board.isPending ? (
        <Skeleton className="h-40 w-full" label={t('queues.heading')} />
      ) : (
        <div className="flex flex-col gap-(--space-md) lg:flex-row">
          <div className="flex-1">
            <QueueBoardTable
              board={board.data}
              series={series}
              queueIds={queueIds}
              onDrillDown={setOpenQueueId}
            />
            <p className="pt-(--space-sm) text-(length:--font-size-caption) text-(--color-text-muted)">
              {board.data.tenantWide ? t('scope.tenantWide') : t('scope.narrow')}
            </p>
          </div>
          {open === undefined ? null : (
            <div className="w-full lg:w-80">
              <QueueDrilldown
                queueId={open.queueId}
                queueName={open.name}
                onClose={() => setOpenQueueId(undefined)}
              />
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}
